import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FileText, Plus, RefreshCw } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui";
import { ContractCard } from "@/components/contracts/ContractCard";
import { CreateContractModal } from "@/components/contracts/CreateContractModal";
import { ReissueSignupLinkModal } from "@/components/contracts/ReissueSignupLinkModal";
import { useContracts, useCreateContract, useReissueResolveToken } from "@/hooks/contracts";
import { useEffectiveCustomerId } from "@/hooks/useEffectiveCustomerId";
import { usePermissions } from "@/hooks/usePermissions";
import { DashboardPageLayout, DashboardPageLoading, DashboardPageSection } from "@/layouts/DashboardPageLayout";
import { getHttpResponseMessage } from "@/utils/httpError";
import { useToast } from "@/hooks/useToast";
export default function ContractsPage() {
    const { isSuperAdmin, selectedCustomerId } = useEffectiveCustomerId();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isReissueModalOpen, setIsReissueModalOpen] = useState(false);
    const toast = useToast();
    const { canReissueResolveToken } = usePermissions();
    const createContractMutation = useCreateContract();
    const reissueResolveTokenMutation = useReissueResolveToken();
    const shouldEnableQuery = !isSuperAdmin || selectedCustomerId !== undefined;
    const { data: contractsData, isLoading, error } = useContracts(undefined, shouldEnableQuery);
    const newestActiveContract = contractsData?.contracts?.filter((item)=>new Date(item.contract.ExpiryDate) > new Date()).sort((a, b)=>new Date(b.contract.CreatedAt).getTime() - new Date(a.contract.CreatedAt).getTime())[0]?.contract;
    const canShowReissueAction = canReissueResolveToken && !!selectedCustomerId && !!newestActiveContract;
    const createContractAction = isSuperAdmin ? /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-2 sm:flex-row sm:items-center",
        children: [
            canShowReissueAction && /*#__PURE__*/ _jsxs(Button, {
                variant: "simple",
                color: "navy-light",
                className: "inline-flex items-center",
                onClick: ()=>setIsReissueModalOpen(true),
                children: [
                    /*#__PURE__*/ _jsx(RefreshCw, {
                        className: "mr-2 h-4 w-4"
                    }),
                    "Reissue signup link"
                ]
            }),
            /*#__PURE__*/ _jsxs(Button, {
                variant: "simple",
                color: "navy-90",
                className: "inline-flex items-center",
                onClick: ()=>setIsCreateModalOpen(true),
                children: [
                    /*#__PURE__*/ _jsx(Plus, {
                        className: "mr-2 h-4 w-4"
                    }),
                    "Create New Contract"
                ]
            })
        ]
    }) : null;
    const handleCreateContract = async (data)=>{
        try {
            const result = await createContractMutation.mutateAsync(data);
            toast.success("Contract created successfully");
            return result;
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to create contract";
            toast.error(message);
            throw error;
        }
    };
    const createContractModal = isSuperAdmin ? /*#__PURE__*/ _jsx(CreateContractModal, {
        isOpen: isCreateModalOpen,
        onClose: ()=>setIsCreateModalOpen(false),
        onSubmit: handleCreateContract,
        isLoading: createContractMutation.isPending
    }) : null;
    const handleReissueResolveToken = async (resolveTokenExpiryHours)=>{
        if (!newestActiveContract) {
            const error = new Error("No active contract found for this customer");
            toast.error(error.message);
            throw error;
        }
        try {
            const result = await reissueResolveTokenMutation.mutateAsync({
                contractId: newestActiveContract.ContractId,
                resolveTokenExpiryHours
            });
            toast.success("Signup link reissued");
            return result;
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to reissue signup link";
            toast.error(message);
            throw error;
        }
    };
    const reissueSignupLinkModal = canShowReissueAction ? /*#__PURE__*/ _jsx(ReissueSignupLinkModal, {
        contract: newestActiveContract,
        isOpen: isReissueModalOpen,
        isLoading: reissueResolveTokenMutation.isPending,
        onClose: ()=>setIsReissueModalOpen(false),
        onConfirm: handleReissueResolveToken
    }) : null;
    if (isLoading) {
        return /*#__PURE__*/ _jsx(DashboardPageLayout, {
            title: "Contracts & Usage Plans",
            description: "Manage contracts and licenses",
            actions: createContractAction,
            children: /*#__PURE__*/ _jsx(DashboardPageLoading, {})
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(DashboardPageLayout, {
                    title: "Contracts & Usage Plans",
                    description: "Manage contracts and licenses",
                    actions: createContractAction,
                    children: /*#__PURE__*/ _jsx(DashboardPageSection, {
                        className: "rounded-md border border-red-200 bg-red-50 p-4 text-red-700",
                        children: getHttpResponseMessage(error) || "Failed to load contracts"
                    })
                }),
                createContractModal,
                reissueSignupLinkModal
            ]
        });
    }
    if (!contractsData?.contracts?.length) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(DashboardPageLayout, {
                    title: "Contracts & Usage Plans",
                    description: "Manage contracts and licenses",
                    actions: createContractAction,
                    children: /*#__PURE__*/ _jsxs(DashboardPageSection, {
                        className: "py-12 text-center",
                        children: [
                            /*#__PURE__*/ _jsx(FileText, {
                                className: "mx-auto mb-4 h-12 w-12 text-gray-400"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-gray-500",
                                children: "No contracts found"
                            })
                        ]
                    })
                }),
                createContractModal,
                reissueSignupLinkModal
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(DashboardPageLayout, {
                title: "Contracts & Usage Plans",
                description: "Manage contracts and licenses",
                actions: createContractAction,
                children: /*#__PURE__*/ _jsx(DashboardPageSection, {
                    className: "grid grid-cols-1 gap-6",
                    children: contractsData.contracts.map((contractDetails)=>/*#__PURE__*/ _jsx(ContractCard, {
                            contractDetails: contractDetails
                        }, contractDetails.contract.ContractId))
                })
            }),
            createContractModal,
            reissueSignupLinkModal
        ]
    });
}
